import { buildQueryString } from '../../lib/query-params.ts';
import { apiClient } from '../client.ts';
import type { PaginatedResponse } from '../types.ts';

export type VaultKind =
  | 'ssh_password'
  | 'ssh_key'
  | 'db_password'
  | 'service_api_key'
  | 'os_user'
  | 'generic'
  | 'login'
  | 'api_credential';

export interface VaultItem {
  id: string;
  teamId: string;
  projectId?: string | null;
  name: string;
  kind: VaultKind | string;
  description?: string | null;
  metadata?: Record<string, unknown>;
  tags?: string[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastRotatedAt?: string | null;
  expiresAt?: string | null;
  hasValue: boolean;
  links?: { id: string; resourceType: string; resourceId: string; relation: string }[];
  /** Schema type cached at write time (login | api_credential | ssh_material | generic). */
  schemaType?: string;
  /** Primary secret field for inject/reveal default. */
  primaryField?: string;
  /** Non-secret field key names present in the structured payload. */
  fieldKeys?: string[];
  /** Non-secret public mirrors (username, url, headerName, baseUrl, …). */
  publicFields?: Record<string, string>;
}

export interface CreateVaultItemParams {
  teamId?: string;
  projectId?: string;
  name: string;
  kind: string;
  description?: string;
  /** Primary secret string; optional when `fields` is provided. Mutually exclusive with non-empty fields. */
  payload?: string;
  /** Structured fields; preferred for typed kinds. Mutually exclusive with non-empty payload. */
  fields?: Record<string, string>;
  tags?: string[];
}

export interface UpdateVaultItemParams {
  name?: string;
  description?: string;
  payload?: string;
  fields?: Record<string, string>;
  fieldsMode?: 'replace' | 'merge';
}

export interface RevealVaultItemResult {
  payload: string;
  type?: string;
  primaryField?: string;
  fields?: Record<string, string>;
}

export async function listVaultItems(params?: {
  page?: number;
  pageSize?: number;
  teamId?: string;
  kind?: string;
  search?: string;
  projectId?: string;
}): Promise<PaginatedResponse<VaultItem>> {
  return apiClient.get<PaginatedResponse<VaultItem>>(`/vault/items${buildQueryString(params)}`);
}

export async function getVaultItem(id: string): Promise<VaultItem> {
  return apiClient.get<VaultItem>(`/vault/items/${id}`);
}

export async function createVaultItem(params: CreateVaultItemParams): Promise<VaultItem> {
  return apiClient.post<VaultItem>('/vault/items', params);
}

export interface GenerateVaultItemParams {
  name: string;
  kind?: string;
  teamId?: string;
  projectId?: string;
  description?: string;
  username?: string;
  comment?: string;
  tags?: string[];
  upsert?: boolean;
}

/** Server-generated material (ssh_key ed25519 OpenSSH, password-like kinds, api tokens). */
export async function generateVaultItem(params: GenerateVaultItemParams): Promise<VaultItem> {
  return apiClient.post<VaultItem>('/vault/items/generate', params);
}

export async function updateVaultItem(id: string, params: UpdateVaultItemParams): Promise<VaultItem> {
  return apiClient.patch<VaultItem>(`/vault/items/${id}`, params);
}

export async function deleteVaultItem(id: string): Promise<void> {
  await apiClient.delete(`/vault/items/${id}`);
}

export async function revealVaultItem(id: string): Promise<RevealVaultItemResult> {
  return apiClient.post<RevealVaultItemResult>(`/vault/items/${id}/reveal`);
}

export async function injectVaultItem(
  id: string,
  body: {
    serverId: string;
    path: string;
    mode?: string;
    ttl?: number;
    prefer?: 'sftp' | 'priv_exec' | '';
    /** Field to inject; defaults server-side to primaryField when omitted. */
    field?: string;
    /** Fail if writer size != secret length. */
    verify?: boolean;
  },
): Promise<{
    path: string;
    transport: string;
    expiresAt?: string;
    blobId?: string;
    field?: string;
    sizeBytes?: number;
    verified?: boolean;
  }> {
  return apiClient.post(`/vault/items/${id}/inject`, body);
}

export async function vaultFromCapture(body: {
  handle: string;
  name: string;
  kind: string;
}): Promise<VaultItem> {
  return apiClient.post<VaultItem>('/vault/items/from-capture', body);
}
